import { useNetworkDetails } from "@features/ax/assetRegistry/networks/hooks";
import { ProgressItem } from "@features/ax/shared/progressItem";
import { logEvent } from "@shared/analytics/analytics";
import { CustomTooltip } from "@shared/components/CustomTooltip";
import { PageHeader } from "@shared/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { TooltipProvider } from "@shared/ui/tooltip";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { Cpu } from "lucide-react";
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { AdditionalNetworkKPIs } from "./AdditionalNetworkKPIs";
import { NetworkHealthSummary } from "./NetworkHealthSummary";
import { WorkOrderMaintenance } from "./workOrderMaintenance";

export const NetworkPerformanceTab: React.FC = () => {
  const remoteUtilityId = getRemoteUtilityId();
  const { id } = useParams<{ id: string }>();
  const { data: performanceData } = useNetworkDetails({
    tab: "performance",
    networkId: id,
    remote_utility_id: remoteUtilityId,
  });
  useEffect(() => {
    logEvent("performance_tab_viewed");
  }, []);

  const waterCards = [
    {
      label: "Non-Revenue Water",
      tooltip:
        "The volume or percentage of water lost within this water transfer system due to leaks, unauthorized consumption, or metering inaccuracies.",
      value: `${
        performanceData?.result?.waterTransferEfficiency?.nonRevenueWater
          .value ?? 0
      }%`,
      status:
        performanceData?.result?.waterTransferEfficiency?.nonRevenueWater
          ?.status || "N/A",
    },
    {
      label: "Average Pressure",
      tooltip:
        "The average water pressure maintained across key points in the network, indicating the ability to deliver water with adequate force.",
      value: `${
        performanceData?.result?.waterTransferEfficiency?.averagePressure
          ?.value || 0
      } ${
        performanceData?.result?.waterTransferEfficiency?.averagePressure
          ?.unit || "psi"
      }`,
      status:
        performanceData?.result?.waterTransferEfficiency?.averagePressure
          ?.status || "N/A",
    },
    {
      label: "Flow Rate (In/Out)",
      tooltip:
        "Real-time or average flow rates at critical entry and exit points of the network, providing insight into movement and demand.",
      value: `${
        performanceData?.result?.waterTransferEfficiency?.flowRate?.in || 0
      }/${
        performanceData?.result?.waterTransferEfficiency?.flowRate?.out || 0
      }`,
      status:
        performanceData?.result?.waterTransferEfficiency?.flowRate?.unit ||
        "MLD",
    },
    {
      label: "Water Quality",
      tooltip:
        "Display of key water quality metrics (e.g., turbidity, chlorine residual, pH) at various points within the network.",
      value:
        performanceData?.result?.waterTransferEfficiency?.waterQuality
          ?.status || "N/A",
      status:
        performanceData?.result?.waterTransferEfficiency?.waterQuality
          ?.parametersOk || "All parameters",
      valueClass: "text-green-600",
    },
  ];

  const healthDist = performanceData?.result?.assetHealthDistribution;

  const assetHealthDistribution = healthDist
    ? [
        {
          condition: "Excellent Condition",
          count: `${healthDist?.excellent?.count} (${healthDist?.excellent?.percentage}%)`,
          color: "bg-green-600",
        },
        {
          condition: "Good Condition",
          count: `${healthDist?.good?.count} (${healthDist?.good?.percentage}%)`,
          color: "bg-green-400",
        },
        {
          condition: "Fair Condition",
          count: `${healthDist?.fair?.count} (${healthDist?.fair?.percentage}%)`,
          color: "bg-yellow-400",
        },
        {
          condition: "Poor Condition",
          count: `${healthDist?.poor?.count} (${healthDist?.poor?.percentage}%)`,
          color: "bg-orange-500",
        },
        {
          condition: "Critical Condition",
          count: `${healthDist?.critical?.count} (${healthDist?.critical?.percentage}%)`,
          color: "bg-red-600",
        },
      ]
    : [];

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
            <Cpu className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <PageHeader
              title="Network Performance Overview"
              subtitle="Comprehensive network operational efficiency, reliability, and
            health metrics"
            />
          </div>
        </div>
        <NetworkHealthSummary data={performanceData?.result} />
        <AdditionalNetworkKPIs data={performanceData?.result} />
        {/* <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Droplets className="h-5 w-5" />
              Water Transfer Efficiency & Quality
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {waterCards.map((card, idx) => (
                <div key={idx} className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <p className="text-sm text-gray-600">{card.label}</p>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-gray-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">{card.tooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <p>{card.value}</p>
                  <p className="text-xs text-gray-500">{card.status}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card> */}

        {/* Network Asset Condition & Reliability */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  Asset Health Status Distribution
                </CardTitle>
                <CustomTooltip
                  message={
                    "Breakdown of assets by condition rating based on inspections, performance data, and maintenance history."
                  }
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {assetHealthDistribution.length > 0 ? (
                  assetHealthDistribution.map((item) => (
                    <div
                      key={item.condition}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 ${item.color} rounded`}></div>
                        <span className="text-sm">{item.condition}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold">{item.count}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>Asset health distribution data not available</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  Asset Age Profile
                </CardTitle>
                <CustomTooltip
                  message={
                    " The distribution of assets across different age ranges,highlighting age demographics of the network."
                  }
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <ProgressItem
                  items={performanceData?.result?.assetAgeProfile}
                  maxValue={100}
                />
              </div>
            </CardContent>
          </Card>
        </div>
        <WorkOrderMaintenance data={performanceData?.result} />
      </div>
    </TooltipProvider>
  );
};
